import React from 'react'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { rectSortingStrategy } from '@dnd-kit/sortable'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Table, { Body, Column, Head, Header, Row } from '../../components/Table'
import Tile, { Grid, GridItem } from '../../components/Tile'
import Action from '../../components/Action'
import Icon from '../../components/Icon'
import { TextLink } from '../../components/type'
import DragHandle from '../../components/drag-and-drop/DragHandle'
import SortableItem from '../../components/drag-and-drop/SortableItem'
import SortableList from '../../components/drag-and-drop/SortableList'
import useSortableSensors from '../../components/drag-and-drop/useSortableSensors'

export const ConnectionIndicator = ({ connected }) => {
  const color = connected ? 'green' : 'red'

  return <span style={{ fontSize: '1.6em', color: color }}>&#9679;</span>
}

const OnlineStatusWrapper = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
`

const OnlineStatus = ({ online }) => {
  return (
    <OnlineStatusWrapper>
      <ConnectionIndicator connected={online} />
      <span style={{ marginLeft: '0.3em' }}>
        {online ? 'online' : 'offline'}
      </span>
    </OnlineStatusWrapper>
  )
}

const SortableTitle = styled.span`
  display: block;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
  text-align: center;
  overflow-wrap: anywhere;
`

// Wraps a tile so it can be reordered by dragging the handle in its title
const SortableTile = ({ id, name, disabled, children }) => {
  const { t } = useTranslation()

  return (
    <SortableItem id={id} disabled={disabled} container={GridItem}>
      {({
        attributes,
        disabled: sortableDisabled,
        listeners,
        setActivatorNodeRef,
      }) =>
        children(
          <SortableTitle>
            <DragHandle
              ref={setActivatorNodeRef}
              type="button"
              disabled={sortableDisabled}
              aria-label={t('actions:module.reorder')}
              {...attributes}
              {...listeners}
              onClick={(event) => event.stopPropagation()}
            >
              <Icon small name="bars" bordered={false} />
            </DragHandle>
            {name}
          </SortableTitle>
        )
      }
    </SortableItem>
  )
}

// Set `sortable` to enable reordering the tiles via drag and drop. `values`
// must then be in the displayed order and `onDragEnd` persists a new order.
const ModuleListPart = ({
  values,
  showTiles,
  itemType,
  onDelete,
  header,
  sortable = false,
  sortingDisabled = false,
  onDragEnd,
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const sensors = useSortableSensors()

  let title = ''
  let url = ''

  switch (itemType) {
    case 'CentralModule':
      title = 'centralModule'
      url = 'modules'
      break
    case 'FieldModule':
      title = 'fieldModule'
      url = 'modules'
      break
    case 'Valve':
      title = 'valve'
      url = 'valves'
      break
    case 'Sensor':
      title = 'sensor'
      url = 'sensors'
      break
    default:
      break
  }

  const noResults = t('common:noResults', {
    results: t(`models:${title}`, { count: 0 }),
  })

  const isModule = ['CentralModule', 'FieldModule'].includes(itemType)
  const showOnlineStatus = isModule

  if (values.length == 0) {
    return (
      <>
        {header}
        <p>{noResults}</p>
      </>
    )
  }

  const renderTile = ({ id, wateringModuleId, online }, tileTitle) => (
    <Tile
      title={tileTitle}
      onClick={() => history.push(`/${url}/${id}`)}
      centered
      actions={
        <Action
          onClick={(e) => {
            history.push(`/map?moduleId=${isModule ? id : wateringModuleId}`)
            e.stopPropagation()
          }}
        >
          <Icon name="map-marker-alt" color="#fff" border="#fff" />
        </Action>
      }
    >
      {showOnlineStatus && <OnlineStatus online={online} />}
    </Tile>
  )

  if (showTiles && sortable) {
    return (
      <>
        {header}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <Grid>
            <SortableList
              items={values.map(({ id }) => id)}
              strategy={rectSortingStrategy}
            >
              {values.map((value) => (
                <SortableTile
                  key={value.id}
                  id={value.id}
                  name={value.name}
                  disabled={sortingDisabled}
                >
                  {(tileTitle) => renderTile(value, tileTitle)}
                </SortableTile>
              ))}
            </SortableList>
          </Grid>
        </DndContext>
      </>
    )
  }

  return showTiles ? (
    <>
      {header}
      <Grid>
        {values.map((value, index) => (
          <GridItem key={index}>{renderTile(value, value.name)}</GridItem>
        ))}
      </Grid>
    </>
  ) : (
    <Table spacious divided>
      <Head>
        <Row>
          <Header colSpan={2}>{t(`models:${title}`, { count: 0 })}</Header>
        </Row>
      </Head>
      <Body>
        {values.map(({ id, name, wateringModuleId }, index) => {
          return (
            <Row key={index}>
              <Column>
                <TextLink to={`/${url}/${id}`}>{name}</TextLink>
              </Column>
              <Column actions={4}>
                <Action
                  horizontal
                  to={`/map?moduleId=${isModule ? id : wateringModuleId}`}
                >
                  <Icon small name="map-marker-alt" />
                </Action>
                <Action horizontal to={`/${url}/${id}`}>
                  <Icon small name="view" />
                </Action>
                <Action horizontal to={`/${url}/${id}/edit`}>
                  <Icon small name="edit" />
                </Action>
                <Action
                  horizontal
                  onClick={(e) => {
                    onDelete(id)
                    e.stopPropagation()
                  }}
                >
                  <Icon small name="delete" />
                </Action>
              </Column>
            </Row>
          )
        })}
      </Body>
    </Table>
  )
}

export default ModuleListPart
